// Capa HTTP del tablero de consumo de IA (`/admin/ia/consumo`,
// `odd/tasks/consumo-de-ia.md`). Tipos + funciones fetch; el estado vive en
// TanStack Query (`src/hooks/useConsumoDeIa.ts`), mismo patrón que
// `lib/mantenedorDeIa.ts` y `lib/feedback.ts`.

import { apiFetch } from "./api";

/**
 * `costo_usd` viaja como STRING decimal (`"0.191250"`) o `null`.
 *
 * `null` es "ningún precio estaba registrado para ese modelo en ese momento",
 * NUNCA "costó cero" — la distinción que sostiene el aviso de "peticiones sin
 * precio" y el rótulo "Sin precio" del log. Va como STRING y no como número
 * por lo mismo que en el resto del sistema: PostgreSQL devuelve un `SUM()`
 * sobre `decimal` como texto para no arriesgar precisión, y el front no
 * reconstruye lo que el servidor ya normalizó.
 */
export type CostoUsd = string | null;

export interface TotalesDeConsumoDeIa {
  requests: number;
  tokens_entrada: number;
  tokens_salida: number;
  tokens_cache_lectura: number;
  tokens_cache_escritura: number;
  tokens_razonamiento: number;
  costo_usd: CostoUsd;
  /** Cuántas de las `requests` no tienen precio: el total las cuenta igual,
      pero no pudo sumar su costo — el aviso existe por esto. */
  requests_sin_precio: number;
}

export interface CostoDeUnDia {
  /** Fecha de calendario `YYYY-MM-DD`, no un instante — la agrupó el SERVIDOR. */
  fecha: string;
  requests: number;
  costo_usd: CostoUsd;
}

export interface ConsumoPorFuncion {
  ia_funcion_id: number | null;
  /** El nombre de la función, o el `origen` (la clase del agente) cuando la
      llamada no tiene función asociada — ya resuelto por el servidor. */
  nombre: string;
  requests: number;
  tokens_entrada: number;
  tokens_salida: number;
  costo_usd: CostoUsd;
}

export interface ConsumoPorModelo {
  proveedor: string;
  modelo: string;
  requests: number;
  tokens_entrada: number;
  tokens_salida: number;
  costo_usd: CostoUsd;
}

export interface ResumenDeConsumoDeIa {
  desde: string;
  hasta: string;
  totales: TotalesDeConsumoDeIa;
  por_dia: CostoDeUnDia[];
  por_funcion: ConsumoPorFuncion[];
  por_modelo: ConsumoPorModelo[];
}

/** Sin `desde`/`hasta`, el SERVIDOR resuelve el mes actual hasta hoy — no se
    duplica ese cálculo acá: se muestra lo que el propio resumen devuelve. */
export interface FiltrosDelResumenDeIa {
  desde?: string;
  hasta?: string;
}

export function fetchResumenDeConsumoDeIa(
  filtros: FiltrosDelResumenDeIa = {},
): Promise<ResumenDeConsumoDeIa> {
  const parametros = new URLSearchParams();
  if (filtros.desde) parametros.set("desde", filtros.desde);
  if (filtros.hasta) parametros.set("hasta", filtros.hasta);

  const cadena = parametros.toString();
  // Sin envelope `data`: el endpoint devuelve el resumen como objeto plano.
  return apiFetch<ResumenDeConsumoDeIa>(`/api/ia/consumos/resumen${cadena ? `?${cadena}` : ""}`);
}

/** Una fila del log de consumo: una petición al proveedor. */
export interface ConsumoDeIa {
  id: number;
  ia_funcion_id: number | null;
  funcion_nombre: string | null;
  user_id: number | null;
  user_nombre: string | null;
  informe_id: number | null;
  informe_numero: string | null;
  proveedor: string;
  modelo: string;
  tokens_entrada: number;
  tokens_salida: number;
  tokens_cache_lectura: number;
  tokens_cache_escritura: number;
  tokens_razonamiento: number;
  costo_usd: CostoUsd;
  /** El nombre corto del agente que disparó la llamada. */
  origen: string;
  invocacion_id: string;
  /** INSTANTE en UTC — se muestra con `formatearFechaYHora`. */
  ocurrido_en: string;
}

/** Los filtros del log, TODOS opcionales y sin default: a diferencia del
    resumen, el log no arranca acotado a un mes (`IndexConsumoDeIaRequest`). */
export interface FiltrosDelLogDeIa {
  iaFuncionId?: number;
  modelo?: string;
  pagina?: number;
}

export interface PaginaDeConsumoDeIa {
  consumos: ConsumoDeIa[];
  total: number;
  pagina: number;
  porPagina: number;
  totalPaginas: number;
}

interface RespuestaPaginadaDeConsumos {
  data: ConsumoDeIa[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export async function fetchConsumosDeIa(
  filtros: FiltrosDelLogDeIa = {},
): Promise<PaginaDeConsumoDeIa> {
  const parametros = new URLSearchParams();
  if (filtros.iaFuncionId) parametros.set("ia_funcion_id", String(filtros.iaFuncionId));
  if (filtros.modelo) parametros.set("modelo", filtros.modelo);
  if (filtros.pagina && filtros.pagina > 1) parametros.set("page", String(filtros.pagina));

  const cadena = parametros.toString();
  const respuesta = await apiFetch<RespuestaPaginadaDeConsumos>(
    `/api/ia/consumos${cadena ? `?${cadena}` : ""}`,
  );

  return {
    consumos: respuesta.data ?? [],
    total: respuesta.meta?.total ?? 0,
    pagina: respuesta.meta?.current_page ?? 1,
    porPagina: respuesta.meta?.per_page ?? 25,
    totalPaginas: respuesta.meta?.last_page ?? 1,
  };
}

/** Una fila del catálogo de precios: un período de vigencia de un modelo. */
export interface IaPrecio {
  id: number;
  proveedor: string;
  modelo: string;
  /** Fecha de calendario `YYYY-MM-DD`. */
  vigente_desde: string;
  usd_entrada_millon: string;
  usd_salida_millon: string;
  /** Nullable: no todos los proveedores cobran la caché aparte. */
  usd_cache_millon: string | null;
}

export interface IaPrecioInput {
  proveedor: string;
  modelo: string;
  vigente_desde: string;
  usd_entrada_millon: string;
  usd_salida_millon: string;
  usd_cache_millon: string | null;
}

export async function fetchPreciosDeIa(): Promise<IaPrecio[]> {
  const { data } = await apiFetch<{ data: IaPrecio[] }>("/api/ia/precios");
  return data ?? [];
}

export async function crearPrecioDeIaRequest(input: IaPrecioInput): Promise<IaPrecio> {
  const { data } = await apiFetch<{ data: IaPrecio }>("/api/ia/precios", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function actualizarPrecioDeIaRequest(
  id: number,
  input: IaPrecioInput,
): Promise<IaPrecio> {
  const { data } = await apiFetch<{ data: IaPrecio }>(`/api/ia/precios/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return data;
}

/** Borra un período de precio. Responde 204: no toca ningún consumo ya
    registrado, su costo ya quedó calculado y congelado. */
export function eliminarPrecioDeIaRequest(id: number): Promise<void> {
  return apiFetch<void>(`/api/ia/precios/${id}`, { method: "DELETE" });
}
